<template>
  <div v-if="!page_loading" class="branded-fare-upsell mb-2">
    <div v-if="upsells_loading">
      <!-- la risposta alle branded tarda leggermente ad arrivare, lascio uno spinner nell'attesa per evitare uno scatto inatteso dell'interfaccia che può spiazzare -->
      <loader class="my-4" :show_timing_slot="true" />
    </div>
    <content-section
      v-else-if="flight_offers_upsell.length > 0"
      icon="flight_ticket"
      title="Tariffe"
      description="Il costo si intende per tutti i passeggeri. I servizi inclusi sono soggetti a regole e condizioni della compagnia e, in alcuni casi, l'esercizio di alcune opzioni incluse (modifiche, cancellazione, ecc.) può richiedere costi aggiuntivi. Verifica sul sito della compagnia aerea le condizioni specifiche delle tariffe proposte."
      no_content_padding
    >
      <swiper
        ref_id="upsell-cards"
        :swiper_params="swiper_params"
        @swiper-initialized="slide_to_fare"
      >
        <swiper-slide
          v-for="offer in flight_offers_upsell"
          :key="offer._key || offer.id"
          style="padding: 10px"
        >
          <branded-fare-card
            :flight_offer="offer"
            :selected_flight_offer_id="selected_flight_offer_id"
            :amenities="offers_amenities[offer.id]"
            @select-flight-offer="handle_select_flight_offer"
          />
        </swiper-slide>
      </swiper>
    </content-section>
  </div>
</template>

<script>
  import {
    get_upsell,
    get_branded_fare,
    find_selected_upsell,
    filter_visible_upsells,
    choose_upsell,
  } from '@liveflight/providers';
  import { select_flight_offer } from '@liveflight/utilities/select-flight-offer';
  import { get_config } from '@/utilities/commons';
  import { map_included_amenities } from './logic';

  import Swiper from '@/components/blocks/swiper.vue';
  import ContentSection from '@liveflight/components/blocks/wrappers/content-section.vue';
  import BrandedFareCard from './branded-fare-card.vue';

  export default {
    name: 'branded-fare-upsell',
    components: {
      swiper: Swiper,
      'content-section': ContentSection,
      'branded-fare-card': BrandedFareCard,
    },
    props: {
      flight_offer: {
        type: Object,
        required: true,
      },
      page_loading: Boolean,
    },
    data() {
      return {
        upsells_loading: true,
        flight_offer_default: {},
        flight_offers_upsell: [],
        swiper_params: {
          classes: 'branded-fare-upsell__cards swiper--overflowing',
          slidesPerView: 1,
          spaceBetween: 0,
          loop: false,
          speed: 1000,
          button_offset: 15,
          controls_size: 12,
          breakpoints: {
            600: {
              hide_controls: true,
              slidesPerView: 3,
            },
          },
        },
        offers_amenities: {},
      };
    },
    computed: {
      travelers: {
        get() {
          return this.$store.state.liveflight.travelers;
        },
        set(value) {
          this.$store.commit('liveflight/travelers', value);
        },
      },
      selected_flight_offer_id() {
        const selected = find_selected_upsell(
          this.flight_offers_upsell,
          this.flight_offer
        );
        const { id = '' } = selected || {};
        return id;
      },
    },
    mounted() {
      this.upsells_loading = true;
      this.$store.commit('liveflight/flight_offer_has_upsell', false);

      // Guard: non chiamare upselling se flight_offer non è valido (es. localStorage corrotto con array vuoto)
      if (!this.flight_offer || !this.flight_offer.id) {
        this.upsells_loading = false;
        return;
      }

      // Capability-gated: provider resolves to null when brandedFareUpsell is off.
      get_upsell(this.flight_offer)
        .then((flight_offer_data) => {
          if (
            !Array.isArray(flight_offer_data) ||
            flight_offer_data.length === 0
          ) {
            return;
          }

          // brandedUpsell.show = 0 disables the block; positive integers
          // cap the number of variants shown (default included).
          const max_count = Math.max(
            0,
            get_config('guiSettings.brandedUpsell.show', 1)
          );
          if (max_count <= 0) {
            this.$store.commit('liveflight/flight_offer_has_upsell', false);
            return;
          }

          const default_offer = flight_offer_data[0];
          const traveler_count = (this.travelers || []).length;

          this.flight_offer_default = default_offer;
          // Sort ascending by raw grandTotal — markup is a flat multiplier
          // so the relative order matches the post-markup displayed prices.
          this.flight_offers_upsell = filter_visible_upsells({
            upsells: flight_offer_data,
            default_offer,
            traveler_count,
            max_count,
          })
            .slice()
            .sort((a, b) => {
              const a_price = Number((a.price && a.price.grandTotal) || 0);
              const b_price = Number((b.price && b.price.grandTotal) || 0);
              return a_price - b_price;
            });

          this.$store.commit(
            'liveflight/flight_offer_has_upsell',
            this.flight_offers_upsell.length > 0
          );

          this.offers_amenities = map_included_amenities(
            this.flight_offers_upsell
          );
        })
        .finally(() => {
          this.upsells_loading = false;
        });
    },
    methods: {
      handle_select_flight_offer(value) {
        if (!value || !value.id) {
          return;
        }

        const item =
          value.id === this.selected_flight_offer_id
            ? this.flight_offer_default
            : value;

        // Provider "pick" action — Amadeus resolves synchronously, others
        // may round-trip to confirm the choice before it hits the store.
        choose_upsell(item).then((chosen) => {
          if (!chosen) {
            return;
          }
          select_flight_offer(chosen);
          this.scroll_top();
        });
      },
      slide_to_fare(swiper_instance) {
        const { swiper } = swiper_instance || {};
        if (!swiper) {
          return;
        }
        // Slide to selected flight offer index
        try {
          const selectedIndex = this.flight_offers_upsell.findIndex(
            (upsell) =>
              get_branded_fare(this.flight_offer) === get_branded_fare(upsell)
          );

          swiper.slideTo(Math.max(0, selectedIndex));
        } catch (e) {
          this.log('branded-fare-upsell.slide_to_fare', e);
        }
      },
    },
  };
</script>

<style lang="scss">
  .branded-fare-upsell {
    &__cards {
      margin: 0 $grid-gutter-width / 3;
    }
    &__separator {
      @extend .panel--separator !optional;
      width: 100%;
      margin-top: 13px;
      margin-bottom: 13px;
    }
  }
</style>
